import React from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  FormControl,
  FormLabel,
  Input,
  Button,
  Box,
  Image,
  VStack,
  HStack,
  InputGroup,
  InputLeftElement,
  useToast,
  useColorModeValue,
  Icon,
} from '@chakra-ui/react';
import { FiDollarSign, FiEdit2, FiPlus, FiUpload } from 'react-icons/fi';
import Select from 'react-select';
import { Dish } from '../../utils/__generated__/graphql';
import { useGetImageUrl } from 'src/hooks';

interface DishModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (formData: any) => Promise<void>;
  dish?: Dish | null;
  categories: Array<{ value: string; label: string }>;
  isLoading: boolean;
  handleFileChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
}

export const DishModal: React.FC<DishModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  dish,
  categories,
  isLoading,
  handleFileChange,
}) => {
  const toast = useToast();
  const modalBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');

  const [formData, setFormData] = React.useState({
    name: '',
    price: '',
    categoryId: '',
    type: 'VEG',
    image: null,
  });

  const { imageUrl } = useGetImageUrl(formData.image);
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  React.useEffect(() => {
    if (dish) {
      setFormData({
        name: dish.name,
        price: dish.price,
        categoryId: dish.categoryId,
        type: dish.type as string,
        image: dish.image || '',
      });
    } else {
      setFormData({
        name: '',
        price: '',
        categoryId: categories[0]?.value || '',
        type: 'VEG',
        image: null,
      });
    }
  }, [dish, categories]);

  // const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
  //   const file = event.target.files?.[0];
  //   if (!file) return;

  //   try {
  //     const compressedFile = await compressImage(file);
  //     const result = await nhost.storage.upload({ file: compressedFile });

  //     if (result?.fileMetadata?.id) {
  //       setFormData((prev) => ({ ...prev, image: result.fileMetadata.id }));
  //       toast({
  //         title: 'Image Uploaded',
  //         status: 'success',
  //         duration: 2000,
  //       });
  //     }
  //   } catch (error: any) {
  //     toast({
  //       title: 'Upload Failed',
  //       description: error.message,
  //       status: 'error',
  //       duration: 3000,
  //     });
  //   }
  // };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name || !formData.price || !formData.categoryId) {
      toast({
        title: 'Validation Error',
        description: 'Please fill in all required fields',
        status: 'error',
        duration: 3000,
      });
      return;
    }
    await onSubmit(formData);
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl" motionPreset="slideInBottom">
      <ModalOverlay backdropFilter="blur(4px)" />
      <ModalContent bg={modalBg}>
        <form onSubmit={handleSubmit}>
          <ModalHeader>{dish ? 'Edit Dish' : 'Add New Dish'}</ModalHeader>
          <ModalCloseButton />

          <ModalBody>
            <VStack spacing={6}>
              {dish && (
                <Box position="relative" width="100%" height="200px" borderRadius="md" overflow="hidden">
                  <Image
                    src={imageUrl || '/assets/img/cover-placeholder.jpg'}
                    alt="Dish preview"
                    objectFit="cover"
                    w="100%"
                    h="100%"
                  />
                  <Box
                    position="absolute"
                    bottom={0}
                    left={0}
                    right={0}
                    p={2}
                    bg="blackAlpha.600"
                    backdropFilter="blur(8px)"
                  >
                    <Button
                      as="label"
                      width="100%"
                      cursor="pointer"
                      leftIcon={<Icon as={FiUpload as any} />}
                      colorScheme="teal"
                      variant="ghost"
                      _hover={{ bg: 'blackAlpha.400' }}
                    >
                      {dish ? 'Update Image' : 'Upload Dish Image'}
                      <input
                        type="file"
                        onChange={handleFileChange}
                        accept="image/*"
                        style={{ display: 'none' }}
                        ref={fileInputRef}
                      />
                    </Button>
                  </Box>
                </Box>
              )}

              <FormControl isRequired>
                <FormLabel>Dish Name</FormLabel>
                <Input
                  placeholder="Enter dish name"
                  value={formData.name}
                  onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
                />
              </FormControl>

              <FormControl isRequired>
                <FormLabel>Price</FormLabel>
                <InputGroup>
                  <InputLeftElement pointerEvents="none">
                    <Icon as={FiDollarSign as any} />
                  </InputLeftElement>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder="0.00"
                    value={formData.price}
                    onChange={(e) => setFormData((prev) => ({ ...prev, price: e.target.value }))}
                  />
                </InputGroup>
              </FormControl>

              <FormControl isRequired>
                <FormLabel>Category</FormLabel>
                <Select
                  options={categories}
                  value={categories.find((cat) => cat.value === formData.categoryId)}
                  onChange={(selected) => setFormData((prev) => ({ ...prev, categoryId: selected?.value || '' }))}
                  styles={{
                    control: (base) => ({
                      ...base,
                      backgroundColor: modalBg,
                      borderColor: borderColor,
                    }),
                    menu: (base) => ({
                      ...base,
                      backgroundColor: modalBg,
                    }),
                  }}
                />
              </FormControl>

              <FormControl isRequired>
                <FormLabel>Type</FormLabel>
                <Select
                  options={[
                    { value: 'VEG', label: 'VEG' },
                    { value: 'NONVEG', label: 'NONVEG' },
                  ]}
                  value={{ value: formData.type, label: formData.type }}
                  onChange={(selected) => setFormData((prev) => ({ ...prev, type: selected?.value || '' }))}
                  styles={{
                    control: (base) => ({
                      ...base,
                      backgroundColor: modalBg,
                      borderColor: borderColor,
                    }),
                    menu: (base) => ({
                      ...base,
                      backgroundColor: modalBg,
                    }),
                  }}
                />
              </FormControl>
            </VStack>
          </ModalBody>

          <ModalFooter>
            <HStack spacing={3}>
              <Button onClick={onClose}>Cancel</Button>
              <Button
                type="submit"
                colorScheme="brand"
                leftIcon={<Icon as={dish ? FiEdit2 : (FiPlus as any)} />}
                isLoading={isLoading}
                loadingText={dish ? 'Updating...' : 'Creating...'}
              >
                {dish ? 'Update Dish' : 'Create Dish'}
              </Button>
            </HStack>
          </ModalFooter>
        </form>
      </ModalContent>
    </Modal>
  );
};
